import { AlertTriangle, Boxes, RefreshCw, Server } from 'lucide-react';
import { useDockerSummary } from '../hooks/useDocker';
import { useHostSummary } from '../hooks/useHost';
import { HostSummaryCard } from '../components/docker/HostSummaryCard';
import { ContainerTable } from '../components/docker/ContainerTable';
import { ServerHealthCard, ServerHealthUnavailable } from '../components/docker/ServerHealthCard';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { getApiErrorMessage, isApiError } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { ComingSoon } from './ComingSoon';

export function Infrastructure() {
  const docker = useDockerSummary();
  const host = useHostSummary();

  const dockerNotConfigured = isApiError(docker.error) && docker.error.response.status === 503;
  const hostNotAvailable = isApiError(host.error) && host.error.response.status === 503;

  if (docker.isLoading && host.isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (dockerNotConfigured && hostNotAvailable) {
    return <ComingSoon icon={Boxes} title="Infrastructure" integrationLabel="Docker" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          {docker.data ? `Live from Docker \u00b7 updated ${formatRelativeTime(docker.data.fetchedAt)}` : 'Server & container health'}
        </p>
        <button
          onClick={() => {
            docker.refetch();
            host.refetch();
          }}
          className="flex items-center gap-1.5 text-sm font-medium text-pulse-600 hover:text-pulse-700 dark:text-pulse-400"
        >
          <RefreshCw size={14} className={docker.isFetching || host.isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <Server size={14} /> Server health
        </h2>
        {host.isLoading ? (
          <Card>
            <CardBody className="flex justify-center py-8">
              <Spinner size={20} />
            </CardBody>
          </Card>
        ) : hostNotAvailable ? (
          <ServerHealthUnavailable />
        ) : host.data ? (
          <ServerHealthCard stats={host.data} />
        ) : (
          <Card className="p-6 text-center text-sm text-slate-400">{getApiErrorMessage(host.error)}</Card>
        )}
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <Boxes size={14} /> Docker
        </h2>
        {docker.isLoading ? (
          <Card>
            <CardBody className="flex justify-center py-8">
              <Spinner size={20} />
            </CardBody>
          </Card>
        ) : dockerNotConfigured ? (
          <Card className="p-6 text-center text-sm text-slate-400">
            Docker isn&apos;t configured - set <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-slate-800">DOCKER_HOST</code> in
            backend/.env.
          </Card>
        ) : docker.isError || !docker.data ? (
          <Card className="p-6 text-center">
            <AlertTriangle className="mx-auto mb-2 text-rose-500" size={22} />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t reach the Docker Engine API</p>
            <p className="mt-1 text-xs text-slate-400">{getApiErrorMessage(docker.error)}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            <HostSummaryCard host={docker.data.host} />
            <Card>
              <CardHeader>
                <CardTitle>Containers</CardTitle>
              </CardHeader>
              <CardBody>
                <ContainerTable containers={docker.data.containers} />
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
